/**
 * Trip list page — shows all user's trips with create button.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MapPin, Trash2, Settings, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FiasAIProvider, FiasAIToggle, useFiasAIContext } from '@/components/fias-ai';
import { ArcheExpandButton } from '../../../components/ArcheExpandButton';
import { useTravelTrips, useTravelListAI, useTravelFeature } from '../hooks';

/** Fias AI toggle button connected to travel AI context. */
function TravelListFiasAIToggle() {
  const fiasAI = useFiasAIContext();
  return <FiasAIToggle onClick={fiasAI.togglePanel} isActive={fiasAI.isPanelOpen} />;
}

/** Standard Fias AI popup wired to the list-page context. */
export function TravelListPage() {
  const navigate = useNavigate();
  const { trips, loading, createTrip, deleteTrip, creating } = useTravelTrips();
  const inboxFeature = useTravelFeature('email_inbox');
  const [newTitle, setNewTitle] = useState('');

  const fiasAICapabilities = useTravelListAI();

  const handleCreate = async () => {
    const result = await createTrip({
      title: newTitle.trim() || 'My Trip',
      startDate: new Date().toISOString().split('T')[0],
    });
    setNewTitle('');
    navigate(`/a/arche_travel/${result.trip.tripId}`);
  };

  return (
    <FiasAIProvider capabilities={fiasAICapabilities}>
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold">Travel Planner</h1>
              <TravelListFiasAIToggle />
              <ArcheExpandButton archeId="arche_travel" />
            </div>
            <div className="flex items-center gap-1">
              {inboxFeature.enabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/a/arche_travel/inbox')}
                  aria-label="Open inbox"
                >
                  <Mail className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/a/arche_travel/settings')}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Create new trip */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex gap-2">
                  <Input
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Trip name (e.g. Grand European Tour)"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreate();
                    }}
                  />
                  <Button onClick={handleCreate} disabled={creating}>
                    <Plus className="h-4 w-4 mr-1" /> New Trip
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Trip list */}
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : trips.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MapPin className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No trips yet. Create your first trip above!</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {trips.map((trip) => (
                    <Card
                      key={trip.tripId}
                      className="cursor-pointer hover:-translate-y-px hover:shadow-sm transition-all"
                      onClick={() => navigate(`/a/arche_travel/${trip.tripId}`)}
                    >
                      <CardContent className="py-3 flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{trip.title}</h3>
                          <p className="text-sm text-muted-foreground">Starting {trip.startDate}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm('Delete this trip?')) {
                              deleteTrip(trip.tripId);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </FiasAIProvider>
  );
}
